import React, { useMemo, useState } from "react";
import { PageContainer } from "@/components/ui/PageContainer";
import { SeoHead } from "@/components/seo/SeoHead";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { BENCHMARKS } from "@/constants/benchmarks";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
const niceStep = (rough: number) => {
  if (!Number.isFinite(rough) || rough <= 0) return 1000;
  const mag = Math.pow(10, Math.floor(Math.log10(rough)));
  const r = rough / mag;
  const n = r <= 1 ? 1 : r <= 2 ? 2 : r <= 5 ? 5 : 10;
  return n * mag;
};

const MONTH_ABBR = [
  "Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"
] as const;
const fmtMonYY = (d: Date) => `${MONTH_ABBR[d.getMonth()]} '${String(d.getFullYear()).slice(-2)}`;

function computeSeries(opts: {
  years: number;
  initial: number;
  monthly: number;
  annualRate: number;
  inflationOn: boolean;
  inflationAnnual: number;
}) {
  const months = Math.max(0, Math.round(opts.years * 12));
  const r = opts.annualRate / 12;
  const infl = opts.inflationAnnual / 12;
  let value = Math.max(0, opts.initial);
  const dataByYear: number[] = [value];
  for (let m = 1; m <= months; m++) {
    value = value * (1 + r) + opts.monthly;
    if (m % 12 === 0) {
      const year = m / 12;
      const adjusted = opts.inflationOn ? value / Math.pow(1 + infl, m) : value;
      dataByYear[year] = adjusted;
    }
  }
  return dataByYear;
}

const HoldingCashVsInvesting: React.FC = () => {
  const [initial, setInitial] = useState<number>(10000);
  const [monthly, setMonthly] = useState<number>(0);
  const [years, setYears] = useState<number>(10);
  const [selected, setSelected] = useState<string[]>(["sp500", "balanced_ca"]);
  const [cashAnnualPct, setCashAnnualPct] = useState<number>(0);
  const [inflationOn, setInflationOn] = useState<boolean>(false);
  const [inflationPct, setInflationPct] = useState<number>(2);

  const benchmarks = BENCHMARKS;

  const seriesMap = useMemo(() => {
    const map: Record<string, number[]> = {};
    for (const b of benchmarks) {
      map[b.id] = computeSeries({
        years,
        initial,
        monthly,
        annualRate: b.rate / 100,
        inflationOn,
        inflationAnnual: inflationPct / 100,
      });
    }
    return map;
  }, [benchmarks, years, initial, monthly, inflationOn, inflationPct]);

  const baseline = useMemo(() =>
    computeSeries({
      years,
      initial,
      monthly,
      annualRate: cashAnnualPct / 100,
      inflationOn,
      inflationAnnual: inflationPct / 100,
    }), [years, initial, monthly, cashAnnualPct, inflationOn, inflationPct]
  );

  

  const chartData = useMemo(() => {
    return Array.from({ length: years + 1 }).map((_, i) => {
      const point: Record<string, number> & { year: number } = {
        year: i,
        baseline: baseline[i] ?? baseline[baseline.length - 1] ?? 0,
      };
      for (const id of selected) {
        const arr = seriesMap[id] || [];
        point[id] = arr[i] ?? arr[arr.length - 1] ?? 0;
      }
      return point as any;
    });
  }, [years, baseline, selected, seriesMap]);

  const primaryId = selected[0];
  const endPrimary = primaryId ? (seriesMap[primaryId]?.[seriesMap[primaryId].length - 1] ?? 0) : 0;

  // Segmented control thumb math (p-1 => 4px inset)
  const pillIndex = useMemo(() => (years === 1 ? 0 : years === 3 ? 1 : years === 5 ? 2 : 3), [years]);
  const thumbInset = 4;
  const thumbLeft = useMemo(() => `calc(${pillIndex * 25}% + ${thumbInset}px)`, [pillIndex]);
  const thumbStyle = useMemo(() => ({ left: thumbLeft, width: `calc(25% - ${thumbInset * 2}px)`, top: thumbInset, height: `calc(100% - ${thumbInset * 2}px)` }), [thumbLeft, thumbInset]);

  const tooltipContent = (tp: any) => {
    const { active, label, payload } = tp || {};
    const n = Number(label);
    if (!active || !Number.isFinite(n) || n <= 0 || !Array.isArray(payload) || payload.length === 0) return null;
    const now = new Date();
    const monthsBack = Math.max(0, Math.floor((years - n) * 12 + 1e-6));
    const d = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
    const labelText = fmtMonYY(d);
    return (
      <div style={{ background: 'white', border: '1px solid rgba(100,116,139,0.3)', borderRadius: 6, padding: '6px 8px', fontSize: 12 }}>
        <div style={{ marginBottom: 4, color: '#475569' }}>{labelText}</div>
        {payload.map((row: any, idx: number) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, background: row.color }} />
              <span>{row.name}</span>
            </div>
            <span>{`$${Math.round(Number(row.value)).toLocaleString()}`}</span>
          </div>
        ))}
      </div>
    );
  };
  const endBaseline = baseline[baseline.length - 1] ?? 0;
  const totalContrib = initial; 

  const yTicks = useMemo(() => {
    const ends = selected.map((id) => {
      const arr = seriesMap[id] || [];
      return arr[arr.length - 1] ?? initial;
    });
    const yMax = Math.max(initial, endBaseline, ...ends);
    const range = Math.max(100, yMax - initial);
    const step = niceStep(range / 4);
    const yMaxNice = initial + Math.ceil(range / step) * step;
    const ticks: number[] = [];
    for (let v = initial; v <= yMaxNice + 1e-6; v += step) {
      ticks.push(Math.round(v));
    }
    return ticks;
  }, [initial, endBaseline, selected, seriesMap]);

  const xTicks = useMemo(() => {
    const ticks: number[] = [];
    if (years <= 1) {
      // Every 2 months, start after axis for readability
      const months = 12;
      const step = 2;
      for (let m = step; m < months; m += step) ticks.push(Number((m / 12).toFixed(4)));
      ticks.push(1);
      return ticks;
    }
    if (years <= 3) {
      // Half-year steps, start at 0.5 to indent from Y axis
      for (let v = 0.5; v < years; v += 0.5) ticks.push(Number(v.toFixed(2)));
      ticks.push(years);
      return ticks;
    }
    // Yearly steps
    if (years >= 10) {
      for (let v = 0; v <= years; v += 1) ticks.push(v);
    } else {
      for (let v = 1; v <= years; v += 1) ticks.push(v);
    }
    return ticks;
  }, [years]);

  

  const reset = () => {
    setInitial(10000);
    setMonthly(0);
    setYears(10);
    setSelected(["sp500", "balanced_ca"]);
    setCashAnnualPct(0);
    setInflationOn(false);
    setInflationPct(2);
  };

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="Holding Cash vs Investing"
        description="Compare how money grows when invested versus held in cash."
        canonicalPath="/tools/holding-cash-vs-investing"
      />

      <header className="bg-background">
        <PageContainer center>
          <div className="pt-1 pb-0 md:pt-2 md:pb-0">
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Holding Cash vs Investing</h1>
            <p className="mt-1 text-sm md:text-base leading-6 md:leading-6 text-muted-foreground max-w-none">
              When you choose <em>not</em> to invest your money, you could be missing out on meaningful returns over time. Give your money a chance to grow by putting it to work.
            </p>
            <p className="mt-3 text-xs md:text-xs leading-5 md:leading-6 text-muted-foreground max-w-none">
              Use this tool to compare key historical benchmarks with not investing at all and see how your portfolio might have progressed over time.
            </p>
          </div>
        </PageContainer>
      </header>

      <main>
        <PageContainer center className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 pt-0 md:pt-0 pb-4 md:items-end">
            <div className="md:col-span-12 hidden md:block">
              <div className="pb-4">
                <div className="text-base font-semibold">Investing vs. staying out of the market</div>
                <div className="text-xs text-muted-foreground"><span className="italic">Estimated additional value from investing in a market benchmark compared with holding cash.</span></div>
              </div>
            </div>
            {/* Controls row (tablet/desktop only) */}
            <div className="hidden md:block md:col-span-7 lg:col-span-8 md:col-start-1 md:row-start-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 items-start">
                <div className="space-y-1 sm:col-span-1">
                  <div className="text-xs font-medium">Investment amount</div>
                  <Input
                    id="initial"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={initial}
                    onChange={(e) => setInitial(clamp(Number(e.target.value || 0), 0, 1_000_000_000))}
                    className="w-32"
                  />
                </div>
                <div className="w-full sm:col-span-2">
                  <div className="mb-1.5 text-xs font-medium text-foreground">Investment time frame</div>
                  <ToggleGroup
                    type="single"
                    size="sm"
                    value={String(years)}
                    onValueChange={(v) => v && setYears(Number(v))}
                    className="relative w-full h-8 rounded-full bg-background p-1 border border-input inline-flex overflow-hidden"
                  >
                    <span
                      aria-hidden
                      className="absolute z-0 rounded-full bg-foreground transition-all duration-200 ease-out pointer-events-none"
                      style={thumbStyle as React.CSSProperties}
                    />
                    <ToggleGroupItem value="1" className="relative z-10 flex-1 basis-0 shrink-0 h-6 text-[11px] leading-none whitespace-nowrap justify-center rounded-full px-3 border-0 focus-visible:ring-0 focus:outline-none outline-none text-foreground data-[state=on]:bg-transparent data-[state=on]:text-background">1 year</ToggleGroupItem>
                    <ToggleGroupItem value="3" className="relative z-10 flex-1 basis-0 shrink-0 h-6 text-[11px] leading-none whitespace-nowrap justify-center rounded-full px-3 border-0 focus-visible:ring-0 focus:outline-none outline-none text-foreground data-[state=on]:bg-transparent data-[state=on]:text-background">3 years</ToggleGroupItem>
                    <ToggleGroupItem value="5" className="relative z-10 flex-1 basis-0 shrink-0 h-6 text-[11px] leading-none whitespace-nowrap justify-center rounded-full px-3 border-0 focus-visible:ring-0 focus:outline-none outline-none text-foreground data-[state=on]:bg-transparent data-[state=on]:text-background">5 years</ToggleGroupItem>
                    <ToggleGroupItem value="10" className="relative z-10 flex-1 basis-0 shrink-0 h-6 text-[11px] leading-none whitespace-nowrap justify-center rounded-full px-3 border-0 focus-visible:ring-0 focus:outline-none outline-none text-foreground data-[state=on]:bg-transparent data-[state=on]:text-background">10 years</ToggleGroupItem>
                  </ToggleGroup>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-1 md:col-span-7 lg:col-span-8 xl:col-span-8 md:col-start-1 md:row-start-3 md:self-end min-w-0">
              <div className="pb-4 md:hidden">
                <div className="text-base font-semibold">Investing vs. staying out of the market</div>
                <div className="text-xs text-muted-foreground"><span className="italic">Estimated additional value from investing in a market benchmark compared with holding cash.</span></div>
              </div>
              <div>
                <div className="mt-0 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 items-start md:hidden">
                  <div className="space-y-1 sm:col-span-1">
                    <div className="text-xs font-medium">Investment amount</div>
                    <Input
                      id="initial"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={initial}
                      onChange={(e) => setInitial(clamp(Number(e.target.value || 0), 0, 1_000_000_000))}
                      className="w-32"
                    />
                  </div>
                  <div className="w-full sm:col-span-2">
                    <div className="mb-1.5 text-xs font-medium text-foreground">Investment time frame</div>
                    <ToggleGroup
                      type="single"
                      size="sm"
                      value={String(years)}
                      onValueChange={(v) => v && setYears(Number(v))}
                      className="relative w-full h-8 rounded-full bg-background p-1 border border-input inline-flex overflow-hidden"
                    >
                      <span
                        aria-hidden
                        className="absolute z-0 rounded-full bg-foreground transition-all duration-200 ease-out pointer-events-none"
                        style={thumbStyle as React.CSSProperties}
                      />
                      <ToggleGroupItem value="1" className="relative z-10 flex-1 basis-0 shrink-0 h-6 text-[11px] leading-none whitespace-nowrap justify-center rounded-full px-3 border-0 focus-visible:ring-0 focus:outline-none outline-none text-foreground data-[state=on]:bg-transparent data-[state=on]:text-background">1 year</ToggleGroupItem>
                      <ToggleGroupItem value="3" className="relative z-10 flex-1 basis-0 shrink-0 h-6 text-[11px] leading-none whitespace-nowrap justify-center rounded-full px-3 border-0 focus-visible:ring-0 focus:outline-none outline-none text-foreground data-[state=on]:bg-transparent data-[state=on]:text-background">3 years</ToggleGroupItem>
                      <ToggleGroupItem value="5" className="relative z-10 flex-1 basis-0 shrink-0 h-6 text-[11px] leading-none whitespace-nowrap justify-center rounded-full px-3 border-0 focus-visible:ring-0 focus:outline-none outline-none text-foreground data-[state=on]:bg-transparent data-[state=on]:text-background">5 years</ToggleGroupItem>
                      <ToggleGroupItem value="10" className="relative z-10 flex-1 basis-0 shrink-0 h-6 text-[11px] leading-none whitespace-nowrap justify-center rounded-full px-3 border-0 focus-visible:ring-0 focus:outline-none outline-none text-foreground data-[state=on]:bg-transparent data-[state=on]:text-background">10 years</ToggleGroupItem>
                    </ToggleGroup>
                  </div>
                </div>
                <div className="h-2 md:hidden" />
                <div className="h-[320px] w-full">
                  <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
                      <XAxis
                        dataKey="year"
                        type="number"
                        tickLine={false}
                        ticks={xTicks}
                        padding={{ left: 0, right: 16 }}
                        domain={[0, years]}
                        tickMargin={6}
                        tickFormatter={(v) => {
                          const now = new Date();
                          const monthsBack = Math.max(0, Math.floor((years - Number(v)) * 12 + 1e-6));
                          const d = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
                          return fmtMonYY(d);
                        }}
                        tick={{ fontSize: 11, fill: '#64748b' }}
                      />
                      <YAxis
                        domain={[initial, yTicks[yTicks.length - 1] ?? initial]}
                        ticks={yTicks}
                        allowDecimals={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        tickFormatter={(v) => `$${Math.round(v).toLocaleString()}`}
                      />
                      <Tooltip content={tooltipContent as any} />
                      {selected.map((id) => {
                        const b = benchmarks.find((x) => x.id === id)!;
                        return (
                          <Line key={id} type="monotone" dataKey={id} name={b.label} stroke={b.color} strokeWidth={2} dot={false} />
                        );
                      })}
                      <Line type="monotone" dataKey="baseline" name="Not invested" stroke="#94a3b8" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            <div className="order-2 md:order-2 md:col-span-5 lg:col-span-4 md:col-start-8 lg:col-start-9 md:row-start-3 md:self-end min-w-0 flex flex-col justify-start md:-translate-y-[2px] lg:-translate-y-[3px] xl:-translate-y-[4px]">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold">Select market benchmarks</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-2">
                    {benchmarks.map((b) => {
                      const isOn = selected.includes(b.id);
                      const toggle = () => setSelected((prev) => {
                        if (prev.includes(b.id)) return prev.filter((x) => x !== b.id);
                        return [...prev, b.id];
                      });
                      return (
                        <div key={b.id} className="flex items-start gap-3 py-1">
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked={isOn}
                            onClick={toggle}
                            className={`shrink-0 h-6 w-6 rounded-md border transition-colors`}
                            style={{
                              borderColor: isOn ? b.color : "#9ca3af",
                              backgroundColor: isOn ? b.color : "transparent",
                            }}
                          />
                          {/* Visually hidden actual checkbox for a11y/state parity */}
                          <Checkbox id={`cb-${b.id}`} checked={isOn} onCheckedChange={toggle} className="sr-only absolute" />
                          <div className="ml-1 min-w-0">
                            <Dialog>
                              <DialogTrigger asChild>
                                <button type="button" className="text-left text-sm underline underline-offset-4 decoration-dotted break-words">
                                  {b.label}
                                </button>
                              </DialogTrigger>
                              <DialogContent className="sm:max-w-xl md:max-w-2xl">
                                <DialogHeader>
                                  <DialogTitle className="text-base">{b.label}</DialogTitle>
                                  <DialogDescription className="text-xs">{b.focus}</DialogDescription>
                                </DialogHeader>
                                <div className="text-sm whitespace-pre-wrap leading-relaxed">{b.desc}</div>
                              </DialogContent>
                            </Dialog>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Results under chart (mobile) and left column at md+ */}
            <div className="order-3 md:order-3 md:col-span-7 lg:col-span-8 xl:col-span-8 md:col-start-1 self-start min-w-0">
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">How much you would have earned investing*</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {selected
                      .map((id) => {
                        const b = benchmarks.find((x) => x.id === id)!;
                        const arr = seriesMap[id] || [];
                        const val = arr[arr.length - 1] ?? 0;
                        return { id, label: b.label, color: b.color, value: val };
                      })
                      .sort((a, b) => b.value - a.value)
                      .map((row) => {
                        const maxVal = Math.max(endBaseline, ...selected.map((sid) => (seriesMap[sid]?.[seriesMap[sid].length - 1] ?? 0)));
                        const pct = maxVal > 0 ? Math.min(100, (row.value / maxVal) * 100) : 0;
                        return (
                          <div key={row.id} className="space-y-0.5">
                            <div className="flex items-center justify-between text-xs">
                              <span>{row.label}</span>
                              <span className="font-medium text-[10px] sm:text-[11px]"><CurrencyAmount value={row.value} currency="CAD" showCurrency /></span>
                            </div>
                            <div className="h-2 w-full rounded bg-muted">
                              <div className="h-2 rounded" style={{ width: `${pct}%`, backgroundColor: row.color }} />
                            </div>
                          </div>
                        );
                      })}
                    <div className="space-y-0.5">
                      <div className="flex items-center justify-between text-xs">
                        <span>Not investing</span>
                        <span className="font-medium text-[10px] sm:text-[11px]"><CurrencyAmount value={endBaseline} currency="CAD" showCurrency /></span>
                      </div>
                      <div className="h-2 w-full rounded bg-muted">
                        <div className="h-2 rounded bg-slate-400/70" style={{ width: "40%" }} />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Bottom line: right column at md+ */}
            <div className="order-4 md:order-4 md:col-span-5 lg:col-span-4 xl:col-span-4 md:col-start-8 lg:col-start-9 self-start">
              <Card className="bg-muted/30 border-muted/60">
                <CardContent className="p-4">
                  <div className="space-y-1.5 text-xs leading-[1.4] text-foreground">
                    <div className="font-semibold text-foreground">The bottom line</div>
                    <p>
                      Investing in public and private markets can give you greater growth potential over time. You can help offset market risk through {" "}
                      <a className="text-primary underline underline-offset-4" href="/content/can/modern-wealth-playbook#alternatives">asset allocation</a>. Use our {" "}
                      <a className="text-primary underline underline-offset-4" href="/portfolio/tools">asset allocation tool</a>{" "}
                      to find a portfolio that best fits you.
                    </p>
                  </div>
                  <div className="mt-4">
                    <Link to="/portfolio/tools" className="block">
                      <Button className="w-full justify-center rounded-md bg-[#2D4C7F] hover:bg-[#243a66] text-white text-sm">Try WealthKit</Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </PageContainer>
      </main>

      {/* Disclaimers */}
      <footer className="pb-10">
        <PageContainer center>
          <div className="mt-1 text-[11px] leading-tight text-muted-foreground max-w-4xl">
            <p className="mb-1.5"><span className="align-middle text-[10px] leading-none">*</span> How much more you would've earned if you'd invested in these market benchmarks vs. not investing at all.</p>
            <p className="mt-1.5">
              All investing is subject to risk, including the possible loss of the money you invest. Past performance is no guarantee of future returns. The performance of an index is not an exact representation of any particular investment, as you cannot invest directly in an index.
            </p>
            <p className="mt-1.5">
              Benchmark comparative indexes represent unmanaged or average returns on various financial assets, which can be compared with funds' total returns for the purpose of measuring relative performance.
            </p>
          </div>
        </PageContainer>
      </footer>
    </div>
  );
};

export default HoldingCashVsInvesting;
